import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { Close, Info } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";

import Styles from "./Settlement.module.css";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import {
  finalSettlementMakerChecker,
  schedulePayouts,
  updateRepScheduleStatus,
  userFinalSettlement,
  userFinalSettlementGet,
  viewSettlement,
} from "../../../../Services/Schedule/schedule";
import Payouts from "../Payouts/Payouts";

// --- CONSTANTS ---
const COLUMN_WIDTHS = {
  sm: "5%",
  md: "10%",
  lg: "20%",
};

// --- SUB-COMPONENT: HEADER ---
const SettlementTableHeader = ({ role }) => (
  <div className={`tableHeading display_flex_row`}>
    <div style={{ width: "10%" }}>From</div>
    <div style={{ width: "10%" }}>To</div>
    <div style={{ width: "20%" }}>Charge Type</div>
    <div style={{ width: "10%" }}>Charge Rate</div>
    <div style={{ width: "10%" }}>
      Est. Amount (₹){" "}
      <Tooltip title={"Estimated amount as per schedule quantum."} arrow>
        <Info
          style={{
            fontSize: 16,
            color: "#666",
            marginLeft: "4px",
          }}
        />
      </Tooltip>
    </div>
    <div style={{ width: "5%" }}>Maker</div>
    <div style={{ width: "5%" }}>Checker</div>
    <div style={{ width: "10%" }}>
      Final Amount (₹){" "}
      <Tooltip title={"Final amount as per delivered quantum."} arrow>
        <Info
          style={{
            fontSize: 16,
            color: "#666",
            marginLeft: "4px",
          }}
        />
      </Tooltip>
    </div>
    <div style={{ width: "10%" }}>Comment</div>
    {(role === "CHECKER" || role === "MAKER") && (
      <div style={{ width: "5%" }}>Verify</div>
    )}
  </div>
);

// --- SUB-COMPONENT: ROW ---
const SettlementRow = ({
  ele,
  index,
  role,
  onAmountChange,
  onCheckerAction,
  rawDamValue,
}) => {
  const isAccepted = (val) =>
    val !== null && (ele?.status === "CHECKER" || ele?.status === "PAYMENT");
  const isCheckerAccepted = (val) => val !== null && ele?.status === "PAYMENT";

  const renderStatusIcon = (accepted) =>
    accepted ? (
      <CheckCircleIcon style={{ fontSize: "14px" }} />
    ) : (
      <HourglassBottomIcon style={{ fontSize: "14px" }} />
    );

  // Determine if input is read-only based on Role and Status
  const isReadOnly = (() => {
    if (role === "MAKER" && ["CHECKER", "PAYMENT"].includes(ele?.status))
      return true;
    if (role === "CHECKER") return true; // Checker generally only verifies
    if (
      (role === "CONSUMER" ||
        role === "GENERATOR" ||
        role === "SUPER_ADMIN" ||
        role === "ADMIN") &&
      ["MAKER", "CHECKER", "PAYMENT"].includes(ele?.status)
    )
      return true;
    return false;
  })();

  // Determine which value to display/edit
  const getInputValue = () => {
    if (role === "MAKER") return ele?.makerInputChargeAmount;
    if (role === "CHECKER")
      return (
        ele?.checkerInputChargeAmount ||
        ele?.makerInputChargeAmount ||
        ele?.userInputChargeAmount
      );
    return ele?.userInputChargeAmount || ele?.amount;
  };

  return (
    <div className="display_flex_row tableDetails">
      <div style={{ width: "10%" }}>{ele?.fromUser}</div>
      <div style={{ width: "10%" }}>{ele?.toUser}</div>
      <div style={{ width: "20%" }}>{ele?.name || ele?.chargeName || "-"}</div>
      <div style={{ width: "10%" }}>
        {(ele?.type || ele?.chargeType) === "PER_MW"
          ? `₹${ele?.value || ele?.chargeValue?.toFixed(3) || 0}/kWh`
          : "Fixed"}{" "}
        <br />
        {ele?.fromUser === "Consumer" && ele?.toUser === "Generator" && (
          <span style={{ fontSize: "8px", color: "#f1935c" }}>
            {rawDamValue}
          </span>
        )}
      </div>
      <div style={{ width: "10%" }}>
        {ele?.amount || ele?.chargeAmount?.toFixed(3) || "-"}
      </div>
      <div
        style={{ width: "5%" }}
        className={
          (role === "MAKER" &&
            (ele?.status === "CHECKER" || ele?.status === "PAYMENT")) ||
          role === "CHECKER" ||
          ((role === "CONSUMER" || role === "GENERATOR") &&
            (ele?.status === "MAKER" ||
              ele?.status === "CHECKER" ||
              ele?.status === "PAYMENT"))
            ? "accepted"
            : "pending"
        }
      >
        {renderStatusIcon(
          (role === "MAKER" &&
            (ele?.status === "CHECKER" || ele?.status === "PAYMENT")) ||
            role === "CHECKER" ||
            ((role === "CONSUMER" || role === "GENERATOR") &&
              (ele?.status === "MAKER" ||
                ele?.status === "CHECKER" ||
                ele?.status === "PAYMENT"))
        )}
      </div>
      <div
        style={{ width: "5%" }}
        className={
          isCheckerAccepted(ele?.checkerInputChargeAmount) ||
          ele?.status === "PAYMENT"
            ? "accepted"
            : "pending"
        }
      >
        {renderStatusIcon(
          isCheckerAccepted(ele?.checkerInputChargeAmount) ||
            ele?.status === "PAYMENT"
        )}
      </div>
      <div style={{ width: "10%" }} className={Styles.finalAmountInput}>
        {isReadOnly || (ele?.isNonEditable && role === "CONSUMER") ? (
          <>{getInputValue()}</>
        ) : (
          <input
            type="text"
            value={getInputValue() || ""}
            onChange={(e) => onAmountChange(e, index)}
          />
        )}
      </div>
      <div style={{ width: "10%" }}>
        {" "}
        {ele?.comment ? (
          <Tooltip title={ele?.comment} arrow>
            <IconButton
              size="small"
              sx={{
                maxWidth: "100%",
                padding: "4px",
                "& .MuiIconButton-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                },
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontSize: "12px",
                  maxWidth: "100%",
                }}
              >
                {ele?.comment}
              </span>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        )}
      </div>
      {(role === "CHECKER" || role === "MAKER") && (
        <div
          style={{
            display: "flex",
            width: "5%",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          {role === "CHECKER" && (
            <>
              {ele?.status === "RECHECK" ? (
                <div className={Styles.verifyIconsBlock}>
                  <CloseIcon style={{ fontSize: "14px" }} />
                </div>
              ) : (
                <div className={Styles.closeIcon}>
                  <CloseIcon
                    style={{ fontSize: "14px" }}
                    onClick={() => onCheckerAction("reject", ele, index)}
                  />
                </div>
              )}
            </>
          )}

          {(role === "CHECKER" && ele?.status !== "PAYMENT") ||
          (role === "MAKER" && ele?.status === "MAKER") ? (
            <div className={Styles.verifyIcons}>
              <DoneIcon
                style={{ fontSize: "14px" }}
                onClick={() => onCheckerAction("accept", ele, index)}
              />
            </div>
          ) : (
            <div className={Styles.verifyIconsBlock}>
              <DoneIcon style={{ fontSize: "14px" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENT: FOOTER BUTTONS ---
const SettlementFooter = ({ role, status, settlementDetails, onAction }) => {
  console.debug("settlementDetails123", settlementDetails);
  const isReadOnlyConsumer =
    (role === "CONSUMER" || role === "GENERATOR") &&
    ["MAKER", "CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyMaker =
    role === "MAKER" && ["CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyChecker = role === "CHECKER" && status === "PAYMENT";

  if (isReadOnlyConsumer || isReadOnlyMaker || isReadOnlyChecker) return null;

  return (
    <div className={`${Styles.settlementModals}`}>
      {role === "CONSUMER" && settlementDetails?.length > 0 && (
        <div
          className={`activeButton ${Styles.continueButton}`}
          onClick={() => onAction("submit")}
        >
          {role === "CONSUMER" ? "Submit" : "SUBMIT"}
        </div>
      )}

      {role === "MAKER" &&
        settlementDetails?.every((ele) => ele?.status === "CHECKER") && (
          <div
            className={`activeButton ${Styles.continueButton}`}
            onClick={() => onAction("submit")}
          >
            {role === "CONSUMER" ? "Submit" : "SUBMIT"}
          </div>
        )}

      {role === "CHECKER" && (
        <>
          <div
            className={`delete_button ${Styles.continueButton}`}
            onClick={() => onAction("rejectAll")}
          >
            Reject All
          </div>
          {settlementDetails?.some((ele) => ele?.status === "RECHECK") && (
            <div
              className={`activeButton ${Styles.continueButton}`}
              onClick={() => onAction("transferToMaker")}
            >
              Transfer to Maker
            </div>
          )}
          {settlementDetails?.every((ele) => ele?.status === "PAYMENT") && (
            <div
              className={`activeButton ${Styles.continueButton}`}
              onClick={() => onAction("payout")}
            >
              SUBMIT
            </div>
          )}
        </>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
const Settlement = ({
  open,
  handleClose,
  id,
  data,
  handleDeliveryStatus,
  admin,
  renderScheuleData,
  isLast,
  selectedScheduleId,
}) => {
  console.debug("data1", data);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole
  );
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId
  );
  const selfCompanyName = useSelector(
    (state) => state?.persistedReducer?.user?.company?.displayName
  );

  const [settlementDetails, setSettlementDetails] = useState([]);
  console.debug("settlementDetails", settlementDetails);
  const [activeRowData, setActiveRowData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    data: "",
    note: "",
    type: null,
  });

  const rawDamValue = (() => {
    const price = data?.calculatedDamPrice || 0;
    const rate = data?.energyRate || 0;

    // If your business logic requires adding the rate back for DAM_MINUS:
    if (data?.energyRateType === "DAM_MINUS") {
      return `DAM-${rate}(DAM=${(price + rate).toFixed(3)})`;
    }
    if (data?.energyRateType === "DAM_PLUS") {
      return `DAM+${rate}(DAM=${(price - rate).toFixed(3)})`;
    }
    return null;
  })();

  console.debug("rawDamValue", rawDamValue);

  // --- 1. Initial Data Fetch ---
  useEffect(() => {
    const fetchData = async () => {
      if (admin || ["MAKER", "CHECKER", "PAYMENT"].includes(data?.status)) {
        await handleAdminGetSettlement();
      } else {
        await handleGetSettlement();
      }
    };
    fetchData();
  }, [id, data, admin, role]);

  const handleGetSettlement = async () => {
    let res = await viewSettlement({
      id: id,
      connectionId: data?.bilateralConnectionId,
      isForOneDay: true,
      repScheduleId: data?.id,
    });
    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        setSettlementDetails(processConsumerViewData(res?.data?.data, data));
      }
    } else {
      ErrorMessage(res?.data?.error);
      handleClose();
    }
  };

  const handleAdminGetSettlement = async () => {
    let res = await userFinalSettlementGet({
      scheduleId: data?.scheduleId,
      repScheduleId: selectedScheduleId || data?.id,
    });
    if (res?.data?.code === 200) {
      setSettlementDetails(processAdminViewData(res?.data?.data, role));
    }
  };

  // --- 2. Input Handlers ---
  const handleAmountChange = (e, index) => {
    const val = e.target.value;
    // Regex: Matches integers and floats with up to 2 decimal places
    if (!/^\d*\.?\d{0,3}$/.test(val)) return;

    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== index) return ele;
        if (role === "MAKER") return { ...ele, makerInputChargeAmount: val };
        if (role === "CHECKER")
          return { ...ele, checkerInputChargeAmount: val };
        return { ...ele, userInputChargeAmount: val };
      })
    );
  };

  const handleCommentChange = (comment) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) =>
        idx === activeRowData?.index ? { ...ele, comment } : ele
      )
    );
  };

  // --- 3. Action Handlers (Logic) ---

  // Handles row-level Verify/Reject clicks (Checker)
  const handleCheckerAction = (type, rowData, index) => {
    setActiveRowData({ ...rowData, index });
    if (type === "reject") {
      setConfirmModal({
        status: true,
        type: "reject_row",
        data: "Do you want to reject this value?",
        note: "Once rejected, Maker will need to update the values again.",
      });
    } else if (type === "accept") {
      setConfirmModal({
        status: true,
        type: "accept_row",
        data: "Do you want to accept this value?",
        note: "Once done, payment will be transferred  through bank uhandleCheckerActionsing NOCA.",
      });
    }
  };

  // Handles footer button clicks
  const handleFooterAction = (actionType) => {
    if (actionType === "submit") {
      setConfirmModal({
        status: true,
        type: "submit_all",
        data: "Do you want to transfer to Checker?",
        note: "Once transferred, Checker will need to update the status.",
      });
    } else if (actionType === "rejectAll") {
      handleRejectAll();
    } else if (actionType === "transferToMaker") {
      setConfirmModal({
        status: true,
        type: "transfer_maker",
        data: "Do you want to proceed with this value?",
        note: "Once done, payment will be transferred  through bank using NOCA.",
      });
    } else if (actionType === "payout") {
      setPayoutOpen(true);
    }
  };

  // --- 4. Submission Logic ---

  // Main Submit Function (Used for Maker submit, Consumer submit, Checker Transfer)
  const submitSettlementData = async ({ isReject = false } = {}) => {
    // 1. Update status
    let nextStatus =
      role === "MAKER" ? "CHECKER" : role === "CHECKER" ? "PAYMENT" : "MAKER";
    if (isReject) nextStatus = "MAKER";

    const statusRes = await updateRepScheduleStatus({
      id: data?.id,
      status: nextStatus,
    });
    if (statusRes?.data?.code !== 200) {
      return ErrorMessage(statusRes?.data?.error);
    }

    // 2. Prepare Payload
    const payload = prepareSubmissionPayload({
      details: settlementDetails,
      role,
      userId,
      scheduleId: data?.scheduleId,
      isReject,
      data,
      selectedScheduleId,
    });

    console.debug("payload1", payload);

    // 3. API Call
    let res;
    if (role === "MAKER" || role === "CHECKER") {
      res = await finalSettlementMakerChecker(payload);
    } else {
      res = await userFinalSettlement(payload);
    }

    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        handleDeliveryStatus({
          id: data?.id,
          scheduleId: data?.scheduleId,
          settlement: true,
          payment: true,
        });
      } else {
        renderScheuleData();
        handleAdminGetSettlement();
      }
      setConfirmModal({ status: false });
      handleClose();
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handleRejectAll = async () => {
    await submitSettlementData({ isReject: true });
  };

  const handlePayoutsSubmit = async ({ list }) => {
    const payload = list?.map((ele) => ({
      bilateralContractId: data?.bilateralContractId,
      fromUserId: ele.fromUserId,
      toUserId: ele.toUserId,
      transferAmount: ele.totalAmount,
      receiverType:
        ele?.toUser === ele?.fromUser
          ? "REFUND"
          : ele?.toUser === "Consumer"
          ? "CONSUMER"
          : ele?.toUser === "Generator"
          ? "GENERATOR"
          : "OTHERS",
      fromUser: ele?.fromUser,
      toUser: ele?.toUser,
      status: "PENDING",
      transactionType: ele?.transactionType,
      repScheduleId: ele?.repScheduleId,
    }));
    let res = await schedulePayouts(payload);
    if (res?.data?.code === 200) {
      await submitSettlementData();
    }
  };

  // Handle local state updates for Checker (Verify/Reject Row)
  const handleLocalRowUpdate = (type) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        // Compare index instead of ID
        if (idx !== activeRowData?.index) return ele;

        if (type === "accept_row") {
          return {
            ...ele,
            status: role === "MAKER" ? "CHECKER" : "PAYMENT",
            checkerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : null,
            makerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : ele.makerInputChargeAmount,
          };
        }
        if (type === "reject_row") {
          return {
            ...ele,
            status: "RECHECK",
            checkerInputChargeAmount: null,
            makerInputChargeAmount: null,
          };
        }
        return ele;
      })
    );
    setConfirmModal({ status: false, data: "", note: "", type: null });
  };

  // Master Modal Confirm Handler
  const handleModalSubmit = () => {
    const { type } = confirmModal;
    if (type === "reject_row" || type === "accept_row")
      return handleLocalRowUpdate(type);
    if (type === "transfer_maker")
      return submitSettlementData({ isReject: true }); // Logic handled inside prepareSubmissionPayload via status check
    return submitSettlementData();
  };

  return (
    <>
      {payoutOpen && (
        <Payouts
          open={payoutOpen}
          handleClose={() => setPayoutOpen(false)}
          list={settlementDetails}
          handlePayoutsSubmit={handlePayoutsSubmit}
        />
      )}

      {confirmModal.status && (
        <ConfirmationModal
          open={confirmModal.status}
          data={confirmModal.data}
          note={confirmModal.note || data?.note}
          header={"Confirmation"}
          handleSubmit={handleModalSubmit}
          handleClose={() => setConfirmModal({ status: false })}
          handleCommentChange={handleCommentChange}
          commentBox={
            confirmModal.type === "reject_row" ||
            confirmModal.type === "accept_row"
          }
          commentInit={confirmModal.type === "reject_row"}
        />
      )}

      <Modal open={open} onClose={handleClose}>
        <div className={`popupModal ${Styles.settlementMainModal}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">Transaction Details</div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          <div className={Styles.tranScheduleContainer}>
            <SummaryItem
              label="Contract ID"
              value={data?.bilateralContractUniqueId || "NA"}
            />
            <SummaryItem
              label="Buyer"
              value={data?.selfCompanyName || selfCompanyName}
            />
            <SummaryItem label="Seller" value={data?.counterPartyCompanyName} />
            <SummaryItem label="Delivery Date" value={data?.fromDate} />
            <SummaryItem
              label="Time Range"
              value={`${data?.fromBlock?.timeBlock?.split("-")[0]} to ${
                data?.toBlock?.timeBlock?.split("-")[1]
              }`}
            />
            <SummaryItem
              label="Scheduled Volume"
              value={`${data?.scheduleVolume?.toFixed(2)}MWh`}
            />
            <SummaryItem
              label="Delivered Volume"
              value={`${data?.deliveredVolume?.toFixed(2)}MWh`}
            />
            {/* <SummaryItem
              label="Energy Rate"
              value={`${data?.energyRate}₹/kWh`}
            />
            <div className={Styles.selectionDetails}>
              <div>Status</div>
              <div className="pending">{data?.status}</div>
            </div> */}
          </div>

          <div className={Styles.settlementDetails}>
            <SettlementTableHeader role={role} />
            <div
              className={`scroll_bar_y ${Styles.settlementDetailsContainer}`}
            >
              {settlementDetails
                ?.filter((ele) => ele?.isView)
                ?.map((ele, index) => (
                  <SettlementRow
                    key={index}
                    index={index}
                    ele={ele}
                    role={role}
                    onAmountChange={handleAmountChange}
                    onCheckerAction={handleCheckerAction}
                    rawDamValue={rawDamValue}
                  />
                ))}
            </div>
          </div>

          <SettlementFooter
            role={role}
            status={data?.status}
            settlementDetails={settlementDetails}
            onAction={handleFooterAction}
          />
        </div>
      </Modal>
    </>
  );
};

// --- HELPER COMPONENTS ---
const SummaryItem = ({ label, value }) => (
  <div className={Styles.selectionDetails}>
    <div>{label}</div>
    <div>{value}</div>
  </div>
);

export default Settlement;

// --- UTILITY FUNCTIONS (PURE LOGIC) ---

const processConsumerViewData = (responseData, scheduleData) => {
  console.debug("scheduleData", scheduleData, responseData);
  const common = {
    scheduleId: scheduleData?.scheduleId,
    makerId: null,
    checkerId: null,
    actualChargeAmount: null,
    makerInputChargeAmount: null,
    checkerInputChargeAmount: null,
    isView: true,
  };

  const createCharge = (base, overrides) => ({
    ...base,
    ...common,
    ...overrides,
  });

  // 1. Calculate General Details (Consumer -> Generator)
  const genDetails = createCharge({
    fromUser: "Consumer",
    toUser: "Generator",
    name: "Energy Charge",
    frequency: "ONE_TIME",
    type: "PER_MW",
    value:
      scheduleData?.calculatedDamPrice?.toFixed(3) || scheduleData?.energyRate,
    isAdminCharge: false,
    amount: responseData?.totalAmountToGenerator?.toFixed(3),
    userInputChargeAmount: (
      (responseData?.totalAmountToGenerator * scheduleData?.deliveredVolume) /
      scheduleData?.scheduleVolume
    )?.toFixed(3),
  });

  // 2. Calculate Consumer Charges Breakdown
  const processedCharges = (responseData?.consumerChargesBreakDown || []).map(
    (charge) =>
      createCharge(charge, {
        fromUser: "Consumer",
        toUser: charge?.chargeType,
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
      })
  );

  // 3. Calculate Generator Charges Breakdown
  const processedChargesGen = (
    responseData?.generatorChargesBreakDown || []
  ).map((charge) =>
    createCharge(charge, {
      fromUser: "Generator",
      toUser: charge?.chargeType,
      isNonEditable: charge?.isAdminCharge,
      userInputChargeAmount:
        charge?.type === "FIXED"
          ? charge?.amount
          : (
              (charge?.amount * scheduleData?.deliveredVolume) /
              scheduleData?.scheduleVolume
            )?.toFixed(3),
      isView: false,
    })
  );

  /**
   * Helper to calculate the difference (Refund amount)
   * Formula: Original Amount - User Input (Pro-rated) Amount
   */
  const calculateRefund = (items) => {
    return items.reduce((acc, item) => {
      const original = item.amount || 0;
      const calculated = item.userInputChargeAmount || 0;
      return acc + (original - calculated);
    }, 0);
  };

  // Calculate specific refund totals
  const consumerRefundTotal = calculateRefund([
    genDetails,
    ...processedCharges,
  ]);
  const generatorRefundTotal = calculateRefund(processedChargesGen);

  // 4. Create Consumer Refund Object
  const refund = createCharge({
    fromUser: "Consumer",
    toUser: "Consumer",
    name: "Refund Request",
    frequency: "ONE_TIME",
    type: "FIXED",
    // Set the calculated difference as the amount
    amount: 0,
    userInputChargeAmount: consumerRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  // 5. Create Generator Refund Object
  const refundGen = createCharge(refund, {
    fromUser: "Generator",
    toUser: "Generator",
    name: "Refund Request",
    isView: false,
    // Set the calculated difference as the amount
    amount: generatorRefundTotal?.toFixed(3),
    userInputChargeAmount: generatorRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundConMargin = createCharge({
    fromUser: "Consumer",
    toUser: "Consumer",
    name: "Extra Margin",
    frequency: "ONE_TIME",
    type: "FIXED",
    // Set the calculated difference as the amount
    amount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  // 5. Create Generator Refund Object
  const refundGenMargin = createCharge(refund, {
    fromUser: "Generator",
    toUser: "Generator",
    name: "Extra Margin",
    isView: false,
    // Set the calculated difference as the amount
    amount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  return [
    genDetails,
    ...processedCharges,
    refundConMargin,
    refund,
    ...processedChargesGen,
    refundGen,
    refundGenMargin,
  ];
};

const processAdminViewData = (charges, role) => {
  if (!charges) return [];
  return charges.map((charge) => {
    let updatedCharge = { ...charge, isView: true };
    if (role === "MAKER") {
      updatedCharge.makerInputChargeAmount = charge?.userInputChargeAmount || 0;
    } else if (role === "CHECKER") {
      updatedCharge.checkerInputChargeAmount =
        charge?.makerInputChargeAmount || 0;
    }
    return updatedCharge;
  });
};

const prepareSubmissionPayload = ({
  details,
  role,
  userId,
  scheduleId,
  isReject = false,
  data,
  selectedScheduleId,
}) => {
  console.debug("details", details);
  return details.map((ele) => {
    const isMaker = role === "MAKER";
    const isChecker = role === "CHECKER";

    // Status Determination
    let status = "MAKER";
    if (isReject) status = "MAKER";
    else if (isMaker) status = "CHECKER";
    else if (isChecker) status = "PAYMENT";

    // ID Logic
    const selfId = data?.selfPartyEntityId;
    const counterId = data?.counterPartyEntityId;

    // Determine From/To IDs
    let fromUserId = ele?.fromUser === "Consumer" ? selfId : counterId;
    let toUserId;

    if (ele?.fromUser === "Consumer" && ele?.toUser === "Generator") {
      toUserId = ele?.fromUser === "Consumer" ? counterId : selfId;
    } else if (ele?.fromUser === ele?.toUser) {
      // Refund scenarios (Consumer->Consumer or Generator->Generator)
      toUserId = ele?.fromUser === "Consumer" ? selfId : counterId;
    } else {
      // Transfer (Consumer->Generator or Vice Versa)
      toUserId = ele?.toEntityId;
    }

    return {
      id: isMaker || isChecker ? ele?.id : null,
      fromUser: ele?.fromUser,
      fromUserId: ele?.fromUserId || fromUserId,
      toUser: ele?.toUser,
      toUserId: ele?.toUserId || ele?.toEntityId || toUserId,
      chargeName: ele?.name || ele?.chargeName,
      chargeFrequency: ele?.frequency || ele?.chargeFrequency,
      chargeType: ele?.type || ele?.chargeType,
      isAdminCharge: ele?.isAdminCharge || false,
      chargeAmount:
        ele?.amount || ele?.chargeAmount || ele?.userInputChargeAmount || 0,
      scheduleId: scheduleId,
      chargeValue: ele?.value || ele?.chargeValue || 0,
      makerId: isMaker || ele?.makerId ? ele?.makerId || userId : null,
      checkerId: isChecker || ele?.checkerId ? ele?.checkerId || userId : null,
      actualChargeAmount: ele?.actualChargeAmount || ele?.amount,
      userInputChargeAmount: ele?.userInputChargeAmount || 0,
      makerInputChargeAmount: ele?.makerInputChargeAmount,
      checkerInputChargeAmount: isMaker ? 0 : ele?.checkerInputChargeAmount,
      status: status,
      repScheduleId: selectedScheduleId || ele?.repScheduleId,
      comment: ele?.comment,
    };
  });
};
